import { useEffect, useRef } from 'react';

// ==========================================
// 1. YOUR EXISTING WAKE PHASE TRACKING UTILITY
// ==========================================
const WAKE_AUTH_PHASE_TAIL_MS = 15_000;
let wakePhaseUntil = 0;

export function markWakeAuthPhase(leadInMs = 0) {
  wakePhaseUntil = Date.now() + leadInMs + WAKE_AUTH_PHASE_TAIL_MS;
}

export function isWakeAuthPhaseActive() {
  return Date.now() < wakePhaseUntil;
}

export function clearWakeAuthPhase() {
  wakePhaseUntil = 0;
}

// ==========================================
// 2. THE ENGAGEMENT HOOK (COMBINED ENGINE)
// ==========================================
export function useAuthWakeUpGuard(authClient) {
  const isRefreshing = useRef(false);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        if (isRefreshing.current) return;
        isRefreshing.current = true;

        const NETWORK_DELAY = 2500;
        console.log("System wake-up detected. Activating wake auth phase...");

        // A. ACTIVATE YOUR TRACKING SHIELD IMMEDIATELY
        // This tells other APIs/Sendbird to pause or ignore errors for the next ~17.5 seconds
        markWakeAuthPhase(NETWORK_DELAY);

        // B. WAIT FOR WI-FI / NETWORK TO CONNECT
        setTimeout(async () => {
          try {
            if (!navigator.onLine) {
              console.warn("Network still offline after wake-up buffer.");
              isRefreshing.current = false;
              return;
            }

            console.log("Network online. Forcing custom policy token check...");

            // Inspect your custom policy storage timestamp
            const sessionCustomExpiry = localStorage.getItem('YOUR_CUSTOM_SESSION_EXPIRY');
            const now = Date.now();

            if (sessionCustomExpiry && now < Number(sessionCustomExpiry)) {
              // Custom policy says session is still valid! Force a silent token refresh.
              await authClient.acquireTokenSilent({
                forceRefresh: true,
                scopes: ["openid", "profile"]
              });
              console.log("Token successfully synchronized silently after wake-up.");
            } else {
              console.log("Custom session policy has expired. Routing to login.");
              clearWakeAuthPhase(); // Clear tracking early since we are leaving the page
              await authClient.loginRedirect();
            }

          } catch (error) {
            console.error("Silent refresh failed on wake-up:", error);
            
            if (error.name === 'InteractionRequiredAuthError' || error.message?.includes('login_required')) {
              clearWakeAuthPhase();
              authClient.loginRedirect();
            }
          } finally {
            isRefreshing.current = false;
          }
        }, NETWORK_DELAY); 
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [authClient]);
}